import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useInputs } from '@/hooks/useInputs';
import { saveTemplate, searchTemplateModel, searchTemplates } from '@/store/actions/template.actions';
import { ITemplate } from '@/store/models/template.model';
import { selectTemplateGroupByGroupAndName } from '@/store/selectors';
import TemplateViewer from '@/pages/admin/template/TemplateViewer';
import { TemplateFilter } from '@/pages/admin/template/TemplateFilter';
import { Page } from '@/ui-kit/templates/Page';

export interface ITemplateEditorForm {
  design: string;
  template: string;
}

interface ITemplates {
  templates?: ITemplate[];
}

const Templates = ({ templates }: ITemplates) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isInitialized = useRef(false);

  const formMethods = useForm<ITemplateEditorForm>({ defaultValues: { template: templates?.[0]?.fullName } });
  const { control, watch, reset, formState } = formMethods;

  const selectedTemplateName = watch('template');
  const designTemplateWatcher = watch('design');

  const methods = useInputs([{ name: 'template', type: 'select' }], formMethods);

  useEffect(() => {
    if (templates?.length > 0 && !isInitialized.current) {
      reset({ template: templates[0].fullName, design: '' });
      isInitialized.current = true;
    }
  }, [templates, reset]);

  const selectedTemplate = useMemo(
    () => templates?.find((template) => selectedTemplateName === template.fullName),
    [templates, selectedTemplateName],
  );

  const currentModel = useAppSelector(
    (state) =>
      selectedTemplate &&
      selectTemplateGroupByGroupAndName(state, selectedTemplate.group, selectedTemplate.name)?.state?.model,
  );

  const modelError = useAppSelector(
    (state) =>
      selectedTemplate &&
      selectTemplateGroupByGroupAndName(state, selectedTemplate.group, selectedTemplate.name)?.error,
  );

  useEffect(() => {
    selectedTemplate && dispatch(searchTemplateModel(selectedTemplate));
  }, [dispatch, selectedTemplate]);

  useEffect(() => {
    if (currentModel?.design) {
      reset({ design: currentModel.design, template: selectedTemplateName });
    }
  }, [currentModel, reset, selectedTemplateName]);

  const handleSaveTemplate = () => {
    if (selectedTemplate && currentModel)
      dispatch(saveTemplate(selectedTemplate, { ...currentModel, design: designTemplateWatcher }));
    reset(undefined, { keepValues: true });
  };

  return (
    <Page
      {...(!modelError &&
        selectedTemplateName && {
          actions: [
            {
              id: 'template-save-btn',
              color: 'default',
              disabled: !formState.dirtyFields.design || !currentModel?.design,
              type: 'button',
              label: t('pages.administration.templates.saveButton'),
              onClick: handleSaveTemplate,
            },
          ],
        })}
    >
      <TemplateFilter
        templates={templates}
        currentModel={currentModel}
        onTemplateChange={methods.handleTemplateChange}
      />
      <TemplateViewer<ITemplateEditorForm>
        template={selectedTemplate}
        design={designTemplateWatcher}
        templateEditor={{ control, name: 'design' }}
      />
    </Page>
  );
};

const TemplatesPage = () => {
  const dispatch = useAppDispatch();

  const templateGroups = useAppSelector((state) => state.template.templateGroups.state, shallowEqual);

  const templates = useMemo(
    () =>
      templateGroups &&
      Object.typedKeys(templateGroups).reduce((acc: ITemplate[], group) => {
        const templates = templateGroups?.[group].reduce(
          (acc: ITemplate[], template) => (template.state?.template ? acc.concat(template.state?.template) : acc),
          [],
        );

        return acc.concat(templates);
      }, []),
    [templateGroups],
  );

  const handleDispatchSearchTemplates = useCallback(() => dispatch(searchTemplates()), [dispatch]);

  useEffect(() => {
    handleDispatchSearchTemplates();
  }, [handleDispatchSearchTemplates]);

  return <Templates templates={templates} />;
};

export default TemplatesPage;
